import {
  faChurch,
  faChurchDuotone,
  faMusicDuotone,
  faTowerBroadcastDuotone,
} from '@church/ui-icons'
import { Outlet, createFileRoute, redirect } from '@tanstack/react-router'
import { Icon } from '../components/ui.tsx'
import { ensureMe } from '../lib/session.ts'
import { afterSignIn, bouncing, homeUrl, isExternal } from '../lib/site.ts'

/**
 * Signed-out layout (id.forhim.io in hosted environments): centered card on a
 * muted canvas. A live session skips straight to where it was going — these
 * screens are meaningless while signed in.
 */
export const Route = createFileRoute('/_auth')({
  beforeLoad: async ({ context, location }) => {
    const me = await ensureMe(context.queryClient)
    if (!me) return
    const target = afterSignIn(
      new URLSearchParams(location.searchStr).get('redirect') ?? undefined,
      homeUrl(me),
    )
    if (isExternal(target)) {
      // That app keeps sending them back: stop and say so (lib/site.ts).
      if (bouncing(target)) throw redirect({ to: '/apps', search: { stuck: target } })
      throw redirect({ href: target })
    }
    throw redirect({ to: target })
  },
  component: AuthLayout,
})

/** The apps one ForHim account opens, shown under the name. */
const APPS = [
  { label: 'ForChurch', icon: faChurchDuotone },
  { label: 'ForServices', icon: faMusicDuotone },
  { label: 'ForStream', icon: faTowerBroadcastDuotone },
] as const

function AuthLayout() {
  return (
    <div className="flex min-h-full flex-col items-center justify-center bg-surface-sunken px-4 py-10">
      <div className="mb-8 flex flex-col items-center text-center">
        <span className="flex size-12 items-center justify-center rounded-2xl bg-brand-solid text-brand-on-solid">
          <Icon icon={faChurch} className="size-6" />
        </span>
        <span className="mt-3 text-2xl font-bold text-content-default">ForHim</span>
        <p className="mt-1 text-sm text-content-muted">One account for every app</p>
        <ul className="mt-3 flex flex-wrap justify-center gap-x-4 gap-y-1">
          {APPS.map((app) => (
            <li
              key={app.label}
              className="flex items-center gap-1.5 text-sm font-medium text-content-muted"
            >
              <Icon icon={app.icon} className="size-4 text-content-brand" />
              {app.label}
            </li>
          ))}
        </ul>
      </div>
      <div className="w-full max-w-md rounded-xl border border-border-subtle bg-surface-raised p-6 shadow-sm sm:p-8">
        <Outlet />
      </div>
    </div>
  )
}
